import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { itemService } from '../../services/itemService';
import { Item, ItemFilters } from '../../types/item';
import { ItemForm } from './ItemForm';
import { formatCurrency } from '../../utils/formatters';

interface ItemListProps {
  filters: ItemFilters;
}

const itemStatusStyle: Record<string, React.CSSProperties> = {
  active:   { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  inactive: { background: 'rgba(80,80,80,0.15)',   color: '#555570', border: '1px solid rgba(80,80,80,0.2)'   },
};

const lowStockStyle: React.CSSProperties = {
  background: 'rgba(201,140,90,0.12)',
  color: '#c98c5a',
  border: '1px solid rgba(201,140,90,0.25)',
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 3,
  fontFamily: "'Inter', sans-serif",
  fontSize: 8,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  marginLeft: 6,
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const ItemList: React.FC<ItemListProps> = ({ filters }) => {
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: itemsData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['items', filters],
    queryFn: () => itemService.getItems(filters)
  });

  const deleteMutation = useMutation({
    mutationFn: itemService.deleteItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item-stats'] });
    }
  });

  const createMutation = useMutation({
    mutationFn: itemService.createItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item-stats'] });
      setShowForm(false);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      itemService.updateItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item-stats'] });
      setEditingItem(null);
    }
  });

  const handleEdit = (item: Item) => {
    setEditingItem(item);
  };

  const handleDelete = (item: Item) => {
    if (window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      deleteMutation.mutate(item.id);
    }
  };

  const handleFormSubmit = (data: any) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Items</div>
          <button
            disabled
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#8a8778',
              background: 'rgba(239,232,213,0.5)',
              border: '1px solid rgba(92,78,51,0.22)',
              borderRadius: 4,
              padding: '7px 16px',
              cursor: 'not-allowed',
              opacity: 0.5,
            }}
          >
            Add Item
          </button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 36, background: 'rgba(241,245,249,1)', borderRadius: 3 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>Failed to load items</p>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['items'] })}
          style={{ marginTop: 8, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }

  const items = itemsData?.data || [];

  return (
    <>
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Items</div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
              {itemsData?.pagination.totalItems ?? 0} total items
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#d97706',
              background: 'rgba(239,232,213,0.6)',
              border: '1px solid rgba(92,78,51,0.35)',
              borderRadius: 4,
              padding: '7px 16px',
              cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(217,119,6,0.45)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(92,78,51,0.35)')}
          >
            Add Item
          </button>
        </div>

        {items.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <svg
              width="40"
              height="40"
              fill="none"
              stroke="rgba(217,119,6,0.25)"
              viewBox="0 0 24 24"
              style={{ margin: '0 auto 12px', display: 'block' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>NO ITEMS FOUND</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                  <th style={thStyle}>Item</th>
                  <th style={thStyle}>SKU</th>
                  <th style={thStyle}>Unit</th>
                  <th style={thStyle}>Selling Price</th>
                  <th style={thStyle}>Cost Price</th>
                  <th style={thStyle}>Stock</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const inventory = item.inventoryRecords?.[0];
                  const isLowStock = inventory && inventory.quantityOnHand <= inventory.reorderLevel;

                  return (
                    <tr
                      key={item.id}
                      style={{ transition: 'background 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={tdStyle}>
                        <div style={{ color: '#1c2033', fontWeight: 500 }}>{item.name}</div>
                        {item.description && (
                          <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.description}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>{item.sku}</td>
                      <td style={tdStyle}>{item.unit}</td>
                      <td style={tdStyle}>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#fbbf24' }}>
                          {formatCurrency(item.sellingPrice)}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#5b5a52' }}>
                          {formatCurrency(item.costPrice)}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {inventory ? (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ color: isLowStock ? '#c98c5a' : '#5b5a52' }}>
                              {inventory.quantityOnHand}
                            </span>
                            {isLowStock && (
                              <span style={lowStockStyle}>Low Stock</span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#8a8778' }}>—</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          ...(item.isActive ? itemStatusStyle.active : itemStatusStyle.inactive),
                          display: 'inline-block',
                          padding: '3px 10px',
                          borderRadius: 3,
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 8,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                        }}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                          <button
                            onClick={() => handleEdit(item)}
                            style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            disabled={deleteMutation.isPending}
                            style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', background: 'none', border: 'none', cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer', padding: 0, opacity: deleteMutation.isPending ? 0.5 : 1 }}
                          >
                            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {itemsData?.pagination && itemsData.pagination.totalPages > 1 && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
              Showing {((itemsData.pagination.currentPage - 1) * itemsData.pagination.itemsPerPage) + 1}–{Math.min(itemsData.pagination.currentPage * itemsData.pagination.itemsPerPage, itemsData.pagination.totalItems)} of {itemsData.pagination.totalItems} results
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              {[...Array(itemsData.pagination.totalPages)].map((_, i) => {
                const pageNum = i + 1;
                const isCurrentPage = pageNum === itemsData.pagination.currentPage;
                return (
                  <button
                    key={i}
                    style={{
                      padding: '4px 10px',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 9,
                      letterSpacing: '0.1em',
                      background: isCurrentPage ? 'linear-gradient(135deg, #d97706, #b45309)' : 'rgba(239,232,213,0.5)',
                      color: isCurrentPage ? '#1c2033' : '#8a8778',
                      border: isCurrentPage ? 'none' : '1px solid rgba(92,78,51,0.22)',
                      borderRadius: 3,
                      cursor: 'pointer',
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {(showForm || editingItem) && (
        <ItemForm
          item={editingItem || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseForm}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </>
  );
};
